import YAML from 'yaml'
import { parseConfigDefinition, type ConfigDefinition } from '../configs/configDefinition'
import {
  cloneJsonObject,
  cloneJsonValue,
  normalizeJsonValue,
  type JsonObject,
  type JsonValue,
} from '../shared/jsonValues'

const ASSET_REFERENCE_PATTERN = /^asset:\/\/([0-9a-fA-F-]{36})$/

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isJsonObject = (value: JsonValue | undefined): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeName = (value: string) => value.trim().toLowerCase()

const readString = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

const normalizeAssetReference = (value: string) => {
  const trimmed = value.trim()
  const match = trimmed.match(ASSET_REFERENCE_PATTERN)
  if (!match?.[1]) {
    throw new Error(`Asset reference "${value}" must use the form asset://<uuid>.`)
  }
  return `asset://${match[1].toLowerCase()}`
}

const readPercentage = (value: unknown, label: string) => {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value < 0 || value > 100) {
      throw new Error(`${label} must be between 0 and 100.`)
    }
    return value
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    const normalized = trimmed.endsWith('%') ? trimmed.slice(0, -1).trim() : trimmed
    const parsed = Number.parseFloat(normalized)
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      throw new Error(`${label} must be a number like 10 or a percentage like 10%.`)
    }
    return parsed
  }
  throw new Error(`${label} must be a number like 10 or a percentage like 10%.`)
}

const normalizeJson = (value: unknown, label: string): JsonValue => {
  const normalized = normalizeJsonValue(value)
  if (normalized === undefined) {
    throw new Error(`${label} must be valid JSON/YAML data.`)
  }
  return normalized
}

type PathToken = string | number

const parsePath = (path: string): PathToken[] => {
  const trimmed = path.trim()
  if (!trimmed) {
    throw new Error('Path must be a non-empty string.')
  }

  const tokens: PathToken[] = []
  const pattern = /([^[.\]]+)|\[(\d+)\]/g
  let lastIndex = 0

  for (const match of trimmed.matchAll(pattern)) {
    const token = match[0]
    const index = match.index ?? 0
    if (index !== lastIndex && trimmed.slice(lastIndex, index) !== '.') {
      throw new Error(`Invalid path "${path}".`)
    }
    if (match[1]) {
      tokens.push(match[1])
    } else if (match[2]) {
      tokens.push(Number.parseInt(match[2], 10))
    }
    lastIndex = index + token.length
  }

  if (lastIndex !== trimmed.length) {
    throw new Error(`Invalid path "${path}".`)
  }

  return tokens
}

const getOrCreateChild = (
  parent: JsonObject | JsonValue[],
  token: PathToken,
  nextToken: PathToken | undefined
): JsonValue => {
  if (typeof token === 'string') {
    const objectParent = parent as JsonObject
    const existing = objectParent[token]
    if (existing !== undefined) return existing
    const created: JsonValue = typeof nextToken === 'number' ? [] : {}
    objectParent[token] = created
    return created
  }

  const arrayParent = parent as JsonValue[]
  const existing = arrayParent[token]
  if (existing !== undefined) return existing
  const created: JsonValue = typeof nextToken === 'number' ? [] : {}
  arrayParent[token] = created
  return created
}

const setValueAtPath = (root: JsonObject, path: string, value: JsonValue) => {
  const tokens = parsePath(path)
  let current: JsonValue = root

  for (let index = 0; index < tokens.length - 1; index += 1) {
    const token = tokens[index]
    const nextToken = tokens[index + 1]

    if (typeof token === 'string') {
      if (!isJsonObject(current)) {
        throw new Error(`Cannot traverse "${path}" through a non-object value.`)
      }
      current = getOrCreateChild(current, token, nextToken)
      continue
    }

    if (!Array.isArray(current)) {
      throw new Error(`Cannot traverse "${path}" through a non-array value.`)
    }
    current = getOrCreateChild(current, token, nextToken)
  }

  const lastToken = tokens[tokens.length - 1]
  if (typeof lastToken === 'string') {
    if (!isJsonObject(current)) {
      throw new Error(`Cannot set "${path}" on a non-object value.`)
    }
    current[lastToken] = cloneJsonValue(value)
    return
  }

  if (!Array.isArray(current)) {
    throw new Error(`Cannot set "${path}" on a non-array value.`)
  }
  current[lastToken] = cloneJsonValue(value)
}

const removeValueAtPath = (root: JsonObject, path: string) => {
  const tokens = parsePath(path)
  let current: JsonValue = root

  for (let index = 0; index < tokens.length - 1; index += 1) {
    const token = tokens[index]
    if (typeof token === 'string') {
      if (!isJsonObject(current)) return
      current = current[token]
      if (current === undefined) return
      continue
    }
    if (!Array.isArray(current)) return
    current = current[token]
    if (current === undefined) return
  }

  const lastToken = tokens[tokens.length - 1]
  if (typeof lastToken === 'string') {
    if (!isJsonObject(current)) return
    delete current[lastToken]
    return
  }

  if (!Array.isArray(current)) return
  if (lastToken < 0 || lastToken >= current.length) return
  current.splice(lastToken, 1)
}

const normalizeReferenceValue = (value: string) => {
  const trimmed = value.trim()
  if (ASSET_REFERENCE_PATTERN.test(trimmed)) {
    return normalizeAssetReference(trimmed)
  }
  return trimmed
}

const replaceFeatureTargetsInValue = (value: JsonValue, from: string, to: string): JsonValue => {
  if (Array.isArray(value)) {
    return value.map((entry) => replaceFeatureTargetsInValue(entry, from, to))
  }
  if (!isJsonObject(value)) {
    return cloneJsonValue(value)
  }

  const replaced: JsonObject = {}
  Object.entries(value).forEach(([key, entry]) => {
    if (
      key === 'feature' &&
      typeof entry === 'string' &&
      normalizeReferenceValue(entry) === normalizeReferenceValue(from)
    ) {
      replaced[key] = to
      return
    }
    replaced[key] = replaceFeatureTargetsInValue(entry, from, to)
  })
  return replaced
}

export type ReplaceFeatureChange = {
  op: 'replace_feature'
  from: string
  to: string
  feature?: string
}

export type SetFeatureAssetChange = {
  op: 'set_feature_asset'
  feature: string
  to: string
}

export type SetFeatureParamChange = {
  op: 'set_feature_param'
  feature: string
  path: string
  value: JsonValue
}

export type RemoveFeatureParamChange = {
  op: 'remove_feature_param'
  feature: string
  path: string
}

export type ExperimentChange =
  | ReplaceFeatureChange
  | SetFeatureAssetChange
  | SetFeatureParamChange
  | RemoveFeatureParamChange

export type ExperimentPartition = {
  name: string
  percentage?: number
  changes: ExperimentChange[]
}

export type ResolvedExperimentPartition = ExperimentPartition & {
  resolvedPercentage: number
}

export type ExperimentDefinition = {
  config: string
  partition?: string
  partitions: ExperimentPartition[]
}

const parseChange = (value: unknown, partitionName: string, index: number): ExperimentChange => {
  if (!isRecord(value)) {
    throw new Error(`Partition "${partitionName}" change #${index + 1} must be a YAML object.`)
  }

  const op = readString(value.op)
  if (!op) {
    throw new Error(`Partition "${partitionName}" change #${index + 1} must define an "op".`)
  }

  switch (op) {
    case 'replace_feature': {
      const from = readString(value.from)
      const to = readString(value.to)
      const feature = readString(value.feature) || undefined
      if (!from || !to) {
        throw new Error(`Partition "${partitionName}" change #${index + 1} must define "from" and "to".`)
      }
      return { op: 'replace_feature', from, to, feature }
    }
    case 'set_feature_asset': {
      const feature = readString(value.feature)
      const to = readString(value.to)
      if (!feature || !to) {
        throw new Error(`Partition "${partitionName}" change #${index + 1} must define "feature" and "to".`)
      }
      return { op, feature, to }
    }
    case 'set_feature_param': {
      const feature = readString(value.feature)
      const path = readString(value.path)
      if (!feature || !path || !Object.prototype.hasOwnProperty.call(value, 'value')) {
        throw new Error(`Partition "${partitionName}" change #${index + 1} must define "feature", "path", and "value".`)
      }
      return {
        op,
        feature,
        path,
        value: normalizeJson(value.value, `Partition "${partitionName}" change #${index + 1} value`),
      }
    }
    case 'remove_feature_param': {
      const feature = readString(value.feature)
      const path = readString(value.path)
      if (!feature || !path) {
        throw new Error(`Partition "${partitionName}" change #${index + 1} must define "feature" and "path".`)
      }
      return { op, feature, path }
    }
    default:
      throw new Error(`Partition "${partitionName}" change #${index + 1} uses unsupported op "${op}".`)
  }
}

const parsePartition = (value: unknown, index: number): ExperimentPartition => {
  if (!isRecord(value)) {
    throw new Error(`Partition #${index + 1} must be a YAML object.`)
  }

  const name = readString(value.name)
  if (!name) {
    throw new Error(`Partition #${index + 1} must define a non-empty name.`)
  }

  let changes: ExperimentChange[] = []
  if (Array.isArray(value.changes)) {
    changes = value.changes.map((entry, changeIndex) => parseChange(entry, name, changeIndex))
  } else if (value.changes !== undefined) {
    throw new Error(`Partition "${name}" changes must be a YAML list.`)
  }

  return {
    name,
    percentage: readPercentage(value.percentage, `Partition "${name}" percentage`),
    changes,
  }
}

export const parseExperimentDefinition = (content: string): ExperimentDefinition => {
  const parsed = YAML.parse(String(content || ''))
  if (!isRecord(parsed)) {
    throw new Error('Experiment payload must be a YAML object.')
  }

  const config = readString(parsed.config)
  if (!config) {
    throw new Error('Experiment payload must define a non-empty "config" asset reference.')
  }

  if (!Array.isArray(parsed.partitions) || parsed.partitions.length === 0) {
    throw new Error('Experiment payload must define "partitions" as a non-empty YAML list.')
  }

  const partitions = parsed.partitions.map((entry, index) => parsePartition(entry, index))
  const seenNames = new Set<string>()
  partitions.forEach((partition) => {
    const normalized = normalizeName(partition.name)
    if (seenNames.has(normalized)) {
      throw new Error(`Partition names must be unique. Duplicate partition "${partition.name}".`)
    }
    seenNames.add(normalized)
  })

  const selectedPartition = readString(parsed.partition) || undefined
  if (selectedPartition && normalizeName(selectedPartition) !== 'random') {
    const hasMatchingPartition = partitions.some(
      (partition) => normalizeName(partition.name) === normalizeName(selectedPartition)
    )
    if (!hasMatchingPartition) {
      throw new Error(`Experiment partition "${selectedPartition}" was not found.`)
    }
  }

  return {
    config: normalizeAssetReference(config),
    partition: selectedPartition && normalizeName(selectedPartition) !== 'random' ? selectedPartition : undefined,
    partitions,
  }
}

export const resolveExperimentPartitions = (definition: ExperimentDefinition): ResolvedExperimentPartition[] => {
  const partitionsWithPercentage = definition.partitions.filter((partition) => partition.percentage !== undefined)
  const partitionsWithoutPercentage = definition.partitions.filter((partition) => partition.percentage === undefined)
  const totalPercentage = partitionsWithPercentage.reduce((sum, partition) => sum + (partition.percentage ?? 0), 0)

  if (totalPercentage > 100) {
    throw new Error('Experiment partition percentages must add up to at most 100.')
  }
  if (partitionsWithoutPercentage.length > 1) {
    throw new Error('At most one partition may omit percentage; it receives the remainder.')
  }
  if (partitionsWithoutPercentage.length === 0 && totalPercentage < 100) {
    throw new Error('Experiment partition percentages must add up to 100 unless one partition omits percentage.')
  }

  const remainder = Math.max(0, 100 - totalPercentage)
  return definition.partitions.map((partition) => ({
    ...partition,
    resolvedPercentage: partition.percentage ?? remainder,
  }))
}

export const pickExperimentPartition = (
  definition: ExperimentDefinition,
  randomValue = Math.random()
): ResolvedExperimentPartition => {
  const partitions = resolveExperimentPartitions(definition).filter((partition) => partition.resolvedPercentage > 0)
  if (partitions.length === 0) {
    throw new Error('Experiment must have at least one partition with a positive percentage.')
  }

  const normalizedRandom = Number.isFinite(randomValue) ? Math.min(Math.max(randomValue, 0), 0.999999999) : 0
  const threshold = normalizedRandom * 100
  let cumulative = 0

  for (const partition of partitions) {
    cumulative += partition.resolvedPercentage
    if (threshold < cumulative) {
      return partition
    }
  }

  return partitions[partitions.length - 1]
}

const getFeatureByName = (config: ConfigDefinition, featureName: string) => {
  const normalized = normalizeName(featureName)
  const feature = config.features.find((entry) => normalizeName(entry.name) === normalized)
  if (!feature) {
    throw new Error(`Config feature "${featureName}" was not found.`)
  }
  return feature
}

const applyChange = (config: ConfigDefinition, change: ExperimentChange) => {
  switch (change.op) {
    case 'replace_feature': {
      const features = change.feature ? [getFeatureByName(config, change.feature)] : config.features
      features.forEach((feature) => {
        feature.params = replaceFeatureTargetsInValue(feature.params, change.from, change.to) as JsonObject
      })
      return
    }
    case 'set_feature_asset': {
      const feature = getFeatureByName(config, change.feature)
      feature.asset = normalizeAssetReference(change.to)
      return
    }
    case 'set_feature_param': {
      const feature = getFeatureByName(config, change.feature)
      const nextParams = cloneJsonObject(feature.params)
      setValueAtPath(nextParams, change.path, change.value)
      feature.params = nextParams
      return
    }
    case 'remove_feature_param': {
      const feature = getFeatureByName(config, change.feature)
      const nextParams = cloneJsonObject(feature.params)
      removeValueAtPath(nextParams, change.path)
      feature.params = nextParams
      return
    }
  }
}

export const applyExperimentPartitionToConfigDefinition = (
  configDefinition: ConfigDefinition,
  partition: ExperimentPartition | ResolvedExperimentPartition
): ConfigDefinition => {
  const cloned: ConfigDefinition = {
    root: configDefinition.root,
    features: configDefinition.features.map((feature) => ({
      name: feature.name,
      asset: feature.asset,
      params: cloneJsonObject(feature.params),
    })),
  }

  partition.changes.forEach((change) => applyChange(cloned, change))
  return cloned
}

export const applyExperimentPartitionToConfigContent = (
  configContent: string,
  partition: ExperimentPartition | ResolvedExperimentPartition
): string => {
  const parsedConfig = parseConfigDefinition(configContent)
  const mutatedConfig = applyExperimentPartitionToConfigDefinition(parsedConfig, partition)
  return YAML.stringify({
    root: mutatedConfig.root,
    features: mutatedConfig.features.map((feature) => ({
      name: feature.name,
      asset: feature.asset,
      params: feature.params,
    })),
  })
}
